import { getBFFApiClient } from '@acp/feature/auth';
import {
    APIResponse,
    TermsAndConditions,
    UserEmailNotificationSettings,
    UserSettings,
    UserSettingsUpdate,
} from '@acp/shared';
import HttpStatusCodes from 'http-status-codes';

async function getUserSettings() {
    const response = await getBFFApiClient().get<APIResponse<UserSettings>>('/v1/me/settings');

    if (response.status === HttpStatusCodes.NOT_FOUND) {
        throw new Error('Settings for this user were not found');
    }

    if (response.status === HttpStatusCodes.FORBIDDEN) {
        throw new Error('You do not have access to this user\'s settings');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get settings');
    }

    return response.data.data;
}

async function updateSettings(settings: UserSettingsUpdate) {
    const response = await getBFFApiClient().patch<APIResponse<UserSettings>>(
        '/v1/me/settings',
        settings
    );

    if (response.status === HttpStatusCodes.NOT_FOUND) {
        throw new Error('Settings not found');
    }

    if (response.status === HttpStatusCodes.FORBIDDEN) {
        throw new Error('You do not have access to update settings');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to update settings');
    }

    return response.data.data;
}

async function getPendingTermsAndConditions() {
    const response = await getBFFApiClient().get<APIResponse<TermsAndConditions>>('/v1/me/terms');

    if (response.status === HttpStatusCodes.NO_CONTENT) {
        return null;
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get pending terms and conditions');
    }

    return response.data.data;
}

async function acceptTermsAndConditions(id: number) {
    const response = await getBFFApiClient().post<APIResponse<null>>(
        `/v1/me/terms/${id}/accept`
    );

    if (response.status === HttpStatusCodes.ACCEPTED) return;

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to accept terms and conditions');
    }
}

async function getEmailNotificationSettings(email: string) {
    const response = await getBFFApiClient()
        .get<APIResponse<UserEmailNotificationSettings>>('/v1/me/email-notification-settings', {
        params: { email },
    });

    if (response.status === HttpStatusCodes.BAD_REQUEST) {
        throw new Error(response.data.error ?? 'Email must belong to a property manager in this agency');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get email notification settings');
    }

    return response.data.data;
}

async function updateEmailNotificationSettings(
    settings: UserEmailNotificationSettings
) {
    const response = await getBFFApiClient()
        .put<APIResponse<UserEmailNotificationSettings>>(
        '/v1/me/email-notification-settings',
        settings
    );

    if (response.status === HttpStatusCodes.BAD_REQUEST) {
        throw new Error(response.data.error ?? 'Email must belong to a property manager in this agency');
    }

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to update email notification settings');
    }

    return response.data.data;
}

export {
    getUserSettings,
    updateSettings,
    getPendingTermsAndConditions,
    acceptTermsAndConditions,
    getEmailNotificationSettings,
    updateEmailNotificationSettings,
};
